import React, { useEffect, useMemo, useState } from 'react';
import { AdminCreateUserRequest, AdminUpdateUserRequest, AdminUserDto, adminApi, isUnauthorized } from '../../services/adminApi';
import { getAdminTheme } from './adminTheme';

interface AdminUsersScreenProps {
  isDarkMode: boolean;
  token: string;
  onUnauthorized: () => void;
}

const AdminUsersScreen: React.FC<AdminUsersScreenProps> = ({ isDarkMode, token, onUnauthorized }) => {
  const [users, setUsers] = useState<AdminUserDto[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const [selectedUserIds, setSelectedUserIds] = useState<Set<number>>(() => new Set());

  const [gridSearch, setGridSearch] = useState('');
  const [sort, setSort] = useState<{ key: keyof AdminUserDto | 'name'; dir: 'asc' | 'desc' }>({ key: 'id', dir: 'asc' });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const [criteriaDraft, setCriteriaDraft] = useState({
    createdFrom: '',
    createdTo: '',
    username: '',
    email: '',
    userGroup: '',
    isActive: 'all' as 'all' | 'yes' | 'no',
    isDeleted: 'no' as 'all' | 'yes' | 'no',
  });
  const [criteriaApplied, setCriteriaApplied] = useState(criteriaDraft);

  const [criteriaCollapsed, setCriteriaCollapsed] = useState(false);
  const [criteriaSize, setCriteriaSize] = useState<'compact' | 'normal' | 'expanded'>('normal');

  const [isAddOpen, setIsAddOpen] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [editUser, setEditUser] = useState<AdminUserDto | null>(null);
  const [resetPassword, setResetPassword] = useState('');
  const [resetError, setResetError] = useState<string | null>(null);
  const [isResetting, setIsResetting] = useState(false);
  const [editDraft, setEditDraft] = useState<AdminUpdateUserRequest>({
    username: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    verificationceode: '',
    verificationceodeFailedAttempts: 0,
    userGroup: '',
    isActive: true,
    subscriptionEndsAtUtc: null,
    eulaAcceptedAtUtc: null,
    acquisitionChannel: '',
    notes: '',
  });
  const [newUser, setNewUser] = useState<AdminCreateUserRequest>(() => ({
    username: '',
    password: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    verificationceode: '',
    userGroup: 'user',
    isActive: true,
    subscriptionEndsAtUtc: null,
    eulaAcceptedAtUtc: null,
    acquisitionChannel: '',
    notes: '',
  }));

  const formatDateTime = (value?: string | null): string => {
    if (!value) return '';
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? '' : d.toLocaleString();
  };

  const toLocalDateTimeInput = (value?: string | null): string => {
    if (!value) return '';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '';
    const pad = (n: number) => n.toString().padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  const refresh = async (opts?: { clearSelection?: boolean }) => {
    setLoading(true);
    setError(null);
    try {
      const data = await adminApi.listUsers(token);
      setUsers(data);
      if (opts?.clearSelection) setSelectedUserIds(new Set());
    } catch (e) {
      if (isUnauthorized(e)) {
        onUnauthorized();
        return;
      }
      setError(e instanceof Error ? e.message : 'Failed loading users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await refresh({ clearSelection: true });
      } finally {
        // ignore
      }
    })();
    return () => {
      cancelled = true;
      void cancelled;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const colors = useMemo(
    () => {
      const theme = getAdminTheme(isDarkMode);
      return {
        theme,
        card: theme.cardBg,
        border: theme.border,
        text: theme.text,
        subText: theme.subText,
        rowHover: theme.hoverBg,
        inputBg: theme.inputBg,
        primary: theme.primary,
        danger: theme.danger,
        success: theme.success,
      };
    },
    [isDarkMode]
  );

  const parseDateFromInput = (value: string): Date | null => {
    const trimmed = value.trim();
    if (!trimmed) return null;
    const d = new Date(trimmed);
    return Number.isNaN(d.getTime()) ? null : d;
  };

  const normalize = (v: unknown): string => (v ?? '').toString().trim().toLowerCase();

  const filteredUsers = useMemo(() => {
    const list = users ?? [];
    const q = normalize(gridSearch);
    const createdFrom = parseDateFromInput(criteriaApplied.createdFrom);
    const createdTo = parseDateFromInput(criteriaApplied.createdTo);

    const result = list.filter((u) => {
      if (criteriaApplied.username.trim().length > 0) {
        if (!normalize(u.username).includes(normalize(criteriaApplied.username))) return false;
      }
      if (criteriaApplied.email.trim().length > 0) {
        if (!normalize(u.email).includes(normalize(criteriaApplied.email))) return false;
      }
      if (criteriaApplied.userGroup.trim().length > 0) {
        if (!normalize(u.userGroup).includes(normalize(criteriaApplied.userGroup))) return false;
      }
      if (criteriaApplied.isActive !== 'all') {
        const isActive = u.isActive !== false;
        if (criteriaApplied.isActive === 'yes' && !isActive) return false;
        if (criteriaApplied.isActive === 'no' && isActive) return false;
      }
      if (criteriaApplied.isDeleted !== 'all') {
        const isDeleted = u.isDeleted === true;
        if (criteriaApplied.isDeleted === 'yes' && !isDeleted) return false;
        if (criteriaApplied.isDeleted === 'no' && isDeleted) return false;
      }

      if (createdFrom || createdTo) {
        const created = new Date(u.createdAtUtc);
        if (!Number.isNaN(created.getTime())) {
          if (createdFrom && created < createdFrom) return false;
          if (createdTo) {
            const createdToEnd = new Date(createdTo);
            createdToEnd.setHours(23, 59, 59, 999);
            if (created > createdToEnd) return false;
          }
        }
      }

      if (q.length > 0) {
        const haystack = [
          u.id,
          u.username,
          u.firstName,
          u.lastName,
          u.email,
          u.phone,
          u.verificationceode,
          u.verificationceodeFailedAttempts,
          u.userGroup,
          u.acquisitionChannel,
          u.notes,
          u.isActive === false ? 'no' : 'yes',
          u.isDeleted ? 'deleted' : 'not deleted',
        ]
          .map((x) => (x ?? '').toString().toLowerCase())
          .join(' | ');
        if (!haystack.includes(q)) return false;
      }

      return true;
    });

    const compare = (a: AdminUserDto, b: AdminUserDto): number => {
      const dir = sort.dir === 'asc' ? 1 : -1;

      const getVal = (u: AdminUserDto): string | number => {
        if (sort.key === 'name') {
          return `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim().toLowerCase();
        }
        const v = (u as any)[sort.key];
        if (typeof v === 'number') return v;
        if (typeof v === 'boolean') return v ? 1 : 0;
        if (sort.key.endsWith('AtUtc')) {
          const d = new Date(v as string);
          const t = Number.isNaN(d.getTime()) ? 0 : d.getTime();
          return t;
        }
        return (v ?? '').toString().toLowerCase();
      };

      const av = getVal(a);
      const bv = getVal(b);
      if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * dir;
      return av.toString().localeCompare(bv.toString()) * dir;
    };

    return result.slice().sort(compare);
  }, [criteriaApplied, gridSearch, sort.dir, sort.key, users]);

  const usernameOptions = useMemo(() => {
    const list = (users ?? [])
      .map((u) => u.username)
      .map((u) => (u ?? '').toString().trim())
      .filter((u) => u.length > 0);
    return Array.from(new Set(list)).sort((a, b) => a.localeCompare(b));
  }, [users]);

  const groupOptions = useMemo(() => {
    const list = (users ?? [])
      .map((u) => u.userGroup ?? '')
      .map((g) => (g ?? '').toString().trim())
      .filter((g) => g.length > 0);
    return Array.from(new Set(list)).sort((a, b) => a.localeCompare(b));
  }, [users]);

  const total = filteredUsers.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const pageSafe = Math.min(Math.max(1, page), totalPages);
  const pageItems = useMemo(() => {
    const start = (pageSafe - 1) * pageSize;
    return filteredUsers.slice(start, start + pageSize);
  }, [filteredUsers, pageSafe, pageSize]);

  useEffect(() => {
    if (page !== pageSafe) setPage(pageSafe);
  }, [page, pageSafe]);

  const toggleSort = (key: keyof AdminUserDto | 'name') => {
    setSort((prev) => {
      if (prev.key !== key) return { key, dir: 'asc' };
      return { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' };
    });
  };

  const sortIndicator = (key: keyof AdminUserDto | 'name'): string => {
    if (sort.key !== key) return '';
    return sort.dir === 'asc' ? ' ▲' : ' ▼';
  };

  const applyCriteria = () => {
    setCriteriaApplied(criteriaDraft);
    setPage(1);
    setSelectedUserIds(new Set());
  };

  const clearCriteria = () => {
    const cleared = {
      createdFrom: '',
      createdTo: '',
      username: '',
      email: '',
      userGroup: '',
      isActive: 'all' as const,
      isDeleted: 'no' as const,
    };
    setCriteriaDraft(cleared);
    setCriteriaApplied(cleared);
    setGridSearch('');
    setPage(1);
    setSelectedUserIds(new Set());
  };

  const criteriaLayout = useMemo(() => {
    if (criteriaSize === 'compact') {
      return {
        minColWidth: 200,
        gap: 8,
        padding: 10,
      };
    }
    if (criteriaSize === 'expanded') {
      return {
        minColWidth: 260,
        gap: 12,
        padding: 14,
      };
    }
    return {
      minColWidth: 220,
      gap: 10,
      padding: 12,
    };
  }, [criteriaSize]);

  const criteriaGridMinWidth = useMemo(() => {
    // Always show 5 fields per row; scroll horizontally if the panel is narrower.
    return 5 * criteriaLayout.minColWidth + 4 * criteriaLayout.gap;
  }, [criteriaLayout.gap, criteriaLayout.minColWidth]);

  const submitAddUser = async () => {
    setAddError(null);
    const username = newUser.username.trim();
    const password = newUser.password.trim();
    if (!username || !password) {
      setAddError('Username and password are required');
      return;
    }

    setIsAdding(true);
    try {
      await adminApi.createUser(token, {
        ...newUser,
        username,
        password,
        firstName: newUser.firstName?.trim() || null,
        lastName: newUser.lastName?.trim() || null,
        email: newUser.email?.trim() || null,
        phone: newUser.phone?.trim() || null,
        verificationceode: newUser.verificationceode?.trim() || null,
        userGroup: newUser.userGroup?.trim() || null,
        acquisitionChannel: newUser.acquisitionChannel?.trim() || null,
        notes: newUser.notes?.trim() || null,
        subscriptionEndsAtUtc: newUser.subscriptionEndsAtUtc || null,
        eulaAcceptedAtUtc: newUser.eulaAcceptedAtUtc || null,
      });

      setIsAddOpen(false);
      setNewUser({
        username: '',
        password: '',
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        verificationceode: '',
        userGroup: 'user',
        isActive: true,
        subscriptionEndsAtUtc: null,
        eulaAcceptedAtUtc: null,
        acquisitionChannel: '',
        notes: '',
      });
      await refresh({ clearSelection: true });
    } catch (e) {
      if (isUnauthorized(e)) {
        onUnauthorized();
        return;
      }
      setAddError(e instanceof Error ? e.message : 'Failed creating user');
    } finally {
      setIsAdding(false);
    }
  };

  const openEdit = (user: AdminUserDto) => {
    setEditError(null);
    setResetError(null);
    setResetPassword('');
    setEditUser(user);
    setEditDraft({
      username: user.username ?? '',
      firstName: user.firstName ?? '',
      lastName: user.lastName ?? '',
      email: user.email ?? '',
      phone: user.phone ?? '',
      verificationceode: user.verificationceode ?? '',
      verificationceodeFailedAttempts: user.verificationceodeFailedAttempts ?? 0,
      userGroup: user.userGroup ?? '',
      isActive: user.isActive !== false,
      subscriptionEndsAtUtc: toLocalDateTimeInput(user.subscriptionEndsAtUtc),
      eulaAcceptedAtUtc: toLocalDateTimeInput(user.eulaAcceptedAtUtc),
      acquisitionChannel: user.acquisitionChannel ?? '',
      notes: user.notes ?? '',
    });
    setIsEditOpen(true);
  };

  const submitEditUser = async () => {
    if (!editUser) return;
    setEditError(null);
    setIsUpdating(true);
    try {
      const updatedUser = await adminApi.updateUser(token, editUser.id, {
        username: editDraft.username?.trim() || null,
        firstName: editDraft.firstName?.trim() || null,
        lastName: editDraft.lastName?.trim() || null,
        email: editDraft.email?.trim() || null,
        phone: editDraft.phone?.trim() || null,
        verificationceode: editDraft.verificationceode?.trim() || null,
        verificationceodeFailedAttempts: Math.max(0, Number(editDraft.verificationceodeFailedAttempts ?? 0) || 0),
        userGroup: editDraft.userGroup?.trim() || null,
        isActive: editDraft.isActive !== false,
        subscriptionEndsAtUtc: editDraft.subscriptionEndsAtUtc || null,
        eulaAcceptedAtUtc: editDraft.eulaAcceptedAtUtc || null,
        acquisitionChannel: editDraft.acquisitionChannel?.trim() || null,
        notes: editDraft.notes?.trim() || null,
      });

      setUsers((prev) => (prev ? prev.map((user) => (user.id === updatedUser.id ? updatedUser : user)) : prev));
      setEditUser(updatedUser);

      setIsEditOpen(false);
      setEditUser(null);
      await refresh({ clearSelection: true });
    } catch (e) {
      if (isUnauthorized(e)) {
        onUnauthorized();
        return;
      }
      setEditError(e instanceof Error ? e.message : 'Failed updating user');
    } finally {
      setIsUpdating(false);
    }
  };

  const submitResetPassword = async () => {
    if (!editUser) return;
    const next = resetPassword.trim();
    if (!next) {
      setResetError('Password is required');
      return;
    }

    setResetError(null);
    setIsResetting(true);
    try {
      await adminApi.resetUserPassword(token, editUser.id, { password: next });
      setResetPassword('');
    } catch (e) {
      if (isUnauthorized(e)) {
        onUnauthorized();
        return;
      }
      setResetError(e instanceof Error ? e.message : 'Failed resetting password');
    } finally {
      setIsResetting(false);
    }
  };

  const deleteSelectedHard = async () => {
    const ids = Array.from(selectedUserIds);
    if (ids.length === 0) return;

    const userLookup = new Map<number, AdminUserDto>();
    (users ?? []).forEach((u) => userLookup.set(u.id, u));

    const label =
      ids.length === 1
        ? (() => {
            const u = userLookup.get(ids[0]);
            return u ? `${u.username} (#${u.id})` : `#${ids[0]}`;
          })()
        : `${ids.length} users`;

    if (!window.confirm(`Delete permanently ${label}? This cannot be undone.`)) return;

    setLoading(true);
    setError(null);
    try {
      for (const id of ids) {
        // eslint-disable-next-line no-await-in-loop
        await adminApi.deleteUserHard(token, id);
      }
      await refresh({ clearSelection: true });
    } catch (e) {
      if (isUnauthorized(e)) {
        onUnauthorized();
        return;
      }
      setError(e instanceof Error ? e.message : 'Failed deleting user');
    } finally {
      setLoading(false);
    }
  };

  const deleteSelectedSoft = async () => {
    const ids = Array.from(selectedUserIds);
    if (ids.length === 0) return;

    const userLookup = new Map<number, AdminUserDto>();
    (users ?? []).forEach((u) => userLookup.set(u.id, u));

    const label =
      ids.length === 1
        ? (() => {
            const u = userLookup.get(ids[0]);
            return u ? `${u.username} (#${u.id})` : `#${ids[0]}`;
          })()
        : `${ids.length} users`;

    if (!window.confirm(`Soft delete ${label}?`)) return;

    setLoading(true);
    setError(null);
    try {
      for (const id of ids) {
        // eslint-disable-next-line no-await-in-loop
        await adminApi.deleteUser(token, id);
      }
      await refresh({ clearSelection: true });
    } catch (e) {
      if (isUnauthorized(e)) {
        onUnauthorized();
        return;
      }
      setError(e instanceof Error ? e.message : 'Failed deleting user');
    } finally {
      setLoading(false);
    }
  };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    height: 34,
    borderRadius: colors.theme.radius,
    border: `1px solid ${colors.border}`,
    backgroundColor: colors.inputBg,
    padding: '0 10px',
    color: colors.text,
    outline: 'none',
    boxSizing: 'border-box',
    fontSize: 13,
    fontWeight: 600,
  };

  const buttonStyle = (variant: 'primary' | 'danger' | 'success' | 'neutral', disabled?: boolean): React.CSSProperties => {
    const theme = colors.theme;

    const bg =
      variant === 'primary'
        ? theme.primary
        : variant === 'danger'
          ? theme.danger
          : variant === 'success'
            ? theme.success
            : theme.cardBg;

    const borderColor = variant === 'neutral' ? theme.border : bg;
    const fg = variant === 'neutral' ? theme.text : '#FFFFFF';

    return {
      height: 34,
      padding: '0 12px',
      border: `1px solid ${borderColor}`,
      borderRadius: theme.radius,
      backgroundColor: disabled ? theme.hoverBg : bg,
      color: disabled ? theme.subText : fg,
      fontWeight: 600,
      fontSize: 13,
      cursor: disabled ? 'not-allowed' : 'pointer',
      whiteSpace: 'nowrap',
    };
  };

  return (
    <div id="admin-users" dir="ltr" style={{ display: 'grid', gap: 14 }}>
      {/* Section 1: Action buttons */}
      <div
        id="admin-users-toolbar"
        style={{
          backgroundColor: colors.card,
          border: `1px solid ${colors.border}`,
          borderRadius: colors.theme.radius,
          padding: 12,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 10,
          flexWrap: 'wrap',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <button
            type="button"
            id="admin-users-add"
            style={buttonStyle('success')}
            onClick={() => {
              setAddError(null);
              setIsAddOpen(true);
            }}
          >
            Add
          </button>

          <button
            type="button"
            id="admin-users-edit"
            style={buttonStyle('primary', selectedUserIds.size !== 1 || loading)}
            disabled={selectedUserIds.size !== 1 || loading}
            onClick={() => {
              const id = Array.from(selectedUserIds)[0];
              const user = (users ?? []).find((u) => u.id === id);
              if (user) openEdit(user);
            }}
          >
            Edit
          </button>

          <button
            type="button"
            id="admin-users-soft-delete"
            title="Soft delete marks the user as deleted (can be recreated later)."
            style={buttonStyle('neutral', selectedUserIds.size === 0 || loading)}
            disabled={selectedUserIds.size === 0 || loading}
            onClick={deleteSelectedSoft}
          >
            Soft delete{selectedUserIds.size > 0 ? ` (${selectedUserIds.size})` : ''}
          </button>

          <button
            type="button"
            id="admin-users-delete"
            title="Delete permanently removes the user from the database."
            style={buttonStyle('danger', selectedUserIds.size === 0 || loading)}
            disabled={selectedUserIds.size === 0 || loading}
            onClick={deleteSelectedHard}
          >
            Delete permanently{selectedUserIds.size > 0 ? ` (${selectedUserIds.size})` : ''}
          </button>

          <button
            type="button"
            id="admin-users-refresh"
            style={buttonStyle('neutral', loading)}
            disabled={loading}
            onClick={() => refresh({ clearSelection: true })}
          >
            Refresh
          </button>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: colors.subText, fontSize: 13, fontWeight: 600 }}>
          <span>{loading ? 'Loading…' : `${total} records`}</span>
          <span style={{ opacity: 0.75 }}>({adminApi.baseUrl()})</span>
        </div>
      </div>

      {/* Section 2: Search criteria */}
      <div
        id="admin-users-criteria"
        style={{
          backgroundColor: colors.card,
          border: `1px solid ${colors.border}`,
          borderRadius: colors.theme.radius,
          padding: criteriaLayout.padding,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: criteriaCollapsed ? 0 : 10 }}>
          <div style={{ color: colors.text, fontSize: 14, fontWeight: 700 }}>Search criteria</div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <select
              value={criteriaSize}
              onChange={(e) => setCriteriaSize(e.target.value as any)}
              aria-label="Criteria size"
              style={{
                ...inputStyle,
                width: 160,
                height: 32,
                fontWeight: 700,
              }}
            >
              <option value="compact">Compact</option>
              <option value="normal">Normal</option>
              <option value="expanded">Expanded</option>
            </select>

            <button type="button" style={buttonStyle('neutral')} onClick={() => setCriteriaCollapsed((v) => !v)}>
              {criteriaCollapsed ? 'Expand' : 'Collapse'}
            </button>
          </div>
        </div>

        {!criteriaCollapsed ? (
          <>
            <div style={{ overflowX: 'auto' }}>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: `repeat(5, minmax(${criteriaLayout.minColWidth}px, 1fr))`,
                  gap: criteriaLayout.gap,
                  minWidth: criteriaGridMinWidth,
                }}
              >
          <div>
            <div style={{ fontSize: 12, fontWeight: 700, color: colors.subText, marginBottom: 6 }}>Created from</div>
            <input
              type="date"
              value={criteriaDraft.createdFrom}
              onChange={(e) => setCriteriaDraft((p) => ({ ...p, createdFrom: e.target.value }))}
              style={inputStyle}
            />
          </div>
          <div>
            <div style={{ fontSize: 12, fontWeight: 700, color: colors.subText, marginBottom: 6 }}>Created to</div>
            <input
              type="date"
              value={criteriaDraft.createdTo}
              onChange={(e) => setCriteriaDraft((p) => ({ ...p, createdTo: e.target.value }))}
              style={inputStyle}
            />
          </div>
          <div>
            <div style={{ fontSize: 12, fontWeight: 700, color: colors.subText, marginBottom: 6 }}>Username</div>
            <input
              list="admin-users-username-options"
              value={criteriaDraft.username}
              onChange={(e) => setCriteriaDraft((p) => ({ ...p, username: e.target.value }))}
              placeholder="Select or type..."
              style={inputStyle}
            />
            <datalist id="admin-users-username-options">
              {usernameOptions.map((u) => (
                <option key={u} value={u} />
              ))}
            </datalist>
          </div>
          <div>
            <div style={{ fontSize: 12, fontWeight: 700, color: colors.subText, marginBottom: 6 }}>Email</div>
            <input
              value={criteriaDraft.email}
              onChange={(e) => setCriteriaDraft((p) => ({ ...p, email: e.target.value }))}
              placeholder="..."
              style={inputStyle}
            />
          </div>
          <div>
            <div style={{ fontSize: 12, fontWeight: 700, color: colors.subText, marginBottom: 6 }}>Group</div>
            <input
              list="admin-users-group-options"
              value={criteriaDraft.userGroup}
              onChange={(e) => setCriteriaDraft((p) => ({ ...p, userGroup: e.target.value }))}
              placeholder="Select or type..."
              style={inputStyle}
            />
            <datalist id="admin-users-group-options">
              {groupOptions.map((g) => (
                <option key={g} value={g} />
              ))}
            </datalist>
          </div>
          <div>
            <div style={{ fontSize: 12, fontWeight: 700, color: colors.subText, marginBottom: 6 }}>Active</div>
            <select
              value={criteriaDraft.isActive}
              onChange={(e) => setCriteriaDraft((p) => ({ ...p, isActive: e.target.value as any }))}
              style={inputStyle}
            >
              <option value="all">All</option>
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </select>
          </div>
          <div>
            <div style={{ fontSize: 12, fontWeight: 700, color: colors.subText, marginBottom: 6 }}>Deleted</div>
            <select
              value={criteriaDraft.isDeleted}
              onChange={(e) => setCriteriaDraft((p) => ({ ...p, isDeleted: e.target.value as any }))}
              style={inputStyle}
            >
              <option value="all">All</option>
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </select>
          </div>
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-start', gap: 10, marginTop: 12, flexWrap: 'wrap' }}>
              <button type="button" style={buttonStyle('primary')} onClick={applyCriteria}>
                Search
              </button>
              <button type="button" style={buttonStyle('neutral')} onClick={clearCriteria}>
                Clear
              </button>

              <div style={{ marginInlineStart: 'auto', display: 'flex', alignItems: 'center', gap: 8, minWidth: 280 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: colors.subText }}>Table search</div>
                <input value={gridSearch} onChange={(e) => setGridSearch(e.target.value)} placeholder="..." style={inputStyle} />
              </div>
            </div>
          </>
        ) : null}
      </div>

      {/* Section 3: Grid */}
      <div
        id="admin-users-grid"
        style={{
          backgroundColor: colors.card,
          border: `1px solid ${colors.border}`,
          borderRadius: colors.theme.radius,
          overflow: 'hidden',
        }}
      >
        {error ? (
          <div id="admin-users-error" style={{ padding: 12, color: isDarkMode ? '#FCA5A5' : '#B91C1C', fontWeight: 900 }}>
            {error}
          </div>
        ) : null}

        <div style={{ overflowX: 'auto' }}>
          <table id="admin-users-table" style={{ width: '100%', borderCollapse: 'collapse', minWidth: 1600 }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${colors.border}` }}>
                <th style={{ textAlign: 'center', padding: '10px 12px', color: colors.subText, fontSize: 12, letterSpacing: 0.3, width: 48 }}>
                  <input
                    type="checkbox"
                    aria-label="Select all rows on page"
                    checked={pageItems.length > 0 && pageItems.every((u) => selectedUserIds.has(u.id))}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setSelectedUserIds((prev) => {
                        const next = new Set(prev);
                        if (checked) {
                          pageItems.forEach((u) => next.add(u.id));
                        } else {
                          pageItems.forEach((u) => next.delete(u.id));
                        }
                        return next;
                      });
                    }}
                    style={{ width: 16, height: 16, accentColor: colors.theme.primary, cursor: 'pointer' }}
                  />
                </th>
                <th
                  onClick={() => toggleSort('id')}
                  style={{ textAlign: 'left', padding: '10px 12px', color: colors.subText, fontSize: 12, letterSpacing: 0.3, cursor: 'pointer' }}
                >
                  Id{sortIndicator('id')}
                </th>
                <th
                  onClick={() => toggleSort('username')}
                  style={{ textAlign: 'left', padding: '10px 12px', color: colors.subText, fontSize: 12, letterSpacing: 0.3, cursor: 'pointer' }}
                >
                  Username{sortIndicator('username')}
                </th>
                <th
                  onClick={() => toggleSort('name')}
                  style={{ textAlign: 'left', padding: '10px 12px', color: colors.subText, fontSize: 12, letterSpacing: 0.3, cursor: 'pointer' }}
                >
                  Name{sortIndicator('name')}
                </th>
                <th
                  onClick={() => toggleSort('email')}
                  style={{ textAlign: 'left', padding: '10px 12px', color: colors.subText, fontSize: 12, letterSpacing: 0.3, cursor: 'pointer' }}
                >
                  Email{sortIndicator('email')}
                </th>
                <th
                  onClick={() => toggleSort('phone')}
                  style={{ textAlign: 'left', padding: '10px 12px', color: colors.subText, fontSize: 12, letterSpacing: 0.3, cursor: 'pointer' }}
                >
                  Phone{sortIndicator('phone')}
                </th>
                <th
                  onClick={() => toggleSort('verificationceode')}
                  style={{ textAlign: 'left', padding: '10px 12px', color: colors.subText, fontSize: 12, letterSpacing: 0.3, cursor: 'pointer' }}
                >
                  verificationceode{sortIndicator('verificationceode')}
                </th>
                <th
                  onClick={() => toggleSort('verificationceodeFailedAttempts')}
                  style={{ textAlign: 'left', padding: '10px 12px', color: colors.subText, fontSize: 12, letterSpacing: 0.3, cursor: 'pointer' }}
                >
                  Failed Attempts{sortIndicator('verificationceodeFailedAttempts')}
                </th>
                <th
                  onClick={() => toggleSort('userGroup')}
                  style={{ textAlign: 'left', padding: '10px 12px', color: colors.subText, fontSize: 12, letterSpacing: 0.3, cursor: 'pointer' }}
                >
                  Group{sortIndicator('userGroup')}
                </th>
                <th
                  onClick={() => toggleSort('isActive')}
                  style={{ textAlign: 'left', padding: '10px 12px', color: colors.subText, fontSize: 12, letterSpacing: 0.3, cursor: 'pointer' }}
                >
                  Active{sortIndicator('isActive')}
                </th>
                <th
                  onClick={() => toggleSort('isDeleted')}
                  style={{ textAlign: 'left', padding: '10px 12px', color: colors.subText, fontSize: 12, letterSpacing: 0.3, cursor: 'pointer' }}
                >
                  Deleted{sortIndicator('isDeleted')}
                </th>
                <th
                  onClick={() => toggleSort('createdAtUtc')}
                  style={{ textAlign: 'left', padding: '10px 12px', color: colors.subText, fontSize: 12, letterSpacing: 0.3, cursor: 'pointer' }}
                >
                  Created{sortIndicator('createdAtUtc')}
                </th>
                <th
                  onClick={() => toggleSort('subscriptionEndsAtUtc')}
                  style={{ textAlign: 'left', padding: '10px 12px', color: colors.subText, fontSize: 12, letterSpacing: 0.3, cursor: 'pointer' }}
                >
                  Subscription{sortIndicator('subscriptionEndsAtUtc')}
                </th>
                <th
                  onClick={() => toggleSort('lastLoginAtUtc')}
                  style={{ textAlign: 'left', padding: '10px 12px', color: colors.subText, fontSize: 12, letterSpacing: 0.3, cursor: 'pointer' }}
                >
                  Last login{sortIndicator('lastLoginAtUtc')}
                </th>
                <th
                  onClick={() => toggleSort('loginCount')}
                  style={{ textAlign: 'left', padding: '10px 12px', color: colors.subText, fontSize: 12, letterSpacing: 0.3, cursor: 'pointer' }}
                >
                  Login count{sortIndicator('loginCount')}
                </th>
                <th
                  onClick={() => toggleSort('notes')}
                  style={{ textAlign: 'left', padding: '10px 12px', color: colors.subText, fontSize: 12, letterSpacing: 0.3, cursor: 'pointer' }}
                >
                  Notes{sortIndicator('notes')}
                </th>
              </tr>
            </thead>
            <tbody>
              {pageItems.map((u) => {
                const isSelected = selectedUserIds.has(u.id);
                return (
                  <tr
                    key={u.id}
                    id={`admin-users-row-${u.id}`}
                    style={{
                      borderTop: `1px solid ${colors.border}`,
                      backgroundColor: isSelected ? (isDarkMode ? colors.theme.hoverBg : '#EEF2FF') : 'transparent',
                    }}
                    onDoubleClick={() => openEdit(u)}
                    onMouseEnter={(e) => {
                      if (isSelected) return;
                      (e.currentTarget as HTMLTableRowElement).style.backgroundColor = colors.rowHover;
                    }}
                    onMouseLeave={(e) => {
                      if (isSelected) return;
                      (e.currentTarget as HTMLTableRowElement).style.backgroundColor = 'transparent';
                    }}
                    onClick={() => {
                      setSelectedUserIds((prev) => {
                        const next = new Set(prev);
                        if (next.has(u.id)) next.delete(u.id);
                        else next.add(u.id);
                        return next;
                      });
                    }}
                  >
                    <td style={{ padding: '10px 12px', textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setSelectedUserIds((prev) => {
                            const next = new Set(prev);
                            if (checked) next.add(u.id);
                            else next.delete(u.id);
                            return next;
                          });
                        }}
                        style={{ width: 16, height: 16, accentColor: colors.theme.primary, cursor: 'pointer' }}
                      />
                    </td>
                    <td style={{ padding: '10px 12px', color: colors.text, fontSize: 13, fontWeight: 900 }}>{u.id}</td>
                    <td style={{ padding: '10px 12px', color: colors.text, fontSize: 13, fontWeight: 900 }}>{u.username}</td>
                    <td style={{ padding: '10px 12px', color: colors.text, fontSize: 13, fontWeight: 800 }}>
                      {`${u.firstName ?? ''} ${u.lastName ?? ''}`.trim()}
                    </td>
                    <td style={{ padding: '10px 12px', color: colors.text, fontSize: 13, fontWeight: 800 }}>{u.email ?? ''}</td>
                    <td style={{ padding: '10px 12px', color: colors.text, fontSize: 13, fontWeight: 800 }}>{u.phone ?? ''}</td>
                    <td style={{ padding: '10px 12px', color: colors.text, fontSize: 13, fontWeight: 800 }}>{u.verificationceode ?? ''}</td>
                    <td style={{ padding: '10px 12px', color: colors.text, fontSize: 13, fontWeight: 900 }}>{u.verificationceodeFailedAttempts ?? 0}</td>
                    <td style={{ padding: '10px 12px', color: colors.text, fontSize: 13, fontWeight: 800 }}>{u.userGroup ?? ''}</td>
                    <td style={{ padding: '10px 12px', color: colors.text, fontSize: 13, fontWeight: 900 }}>
                      {u.isActive === false ? 'No' : 'Yes'}
                    </td>
                    <td
                      style={{
                        padding: '10px 12px',
                        color: u.isDeleted ? (isDarkMode ? '#FCA5A5' : '#B91C1C') : colors.subText,
                        fontSize: 13,
                        fontWeight: 900,
                      }}
                    >
                      {u.isDeleted ? 'Yes' : 'No'}
                    </td>
                    <td style={{ padding: '10px 12px', color: colors.subText, fontSize: 13, fontWeight: 800 }}>{formatDateTime(u.createdAtUtc)}</td>
                    <td style={{ padding: '10px 12px', color: colors.subText, fontSize: 13, fontWeight: 800 }}>
                      {formatDateTime(u.subscriptionEndsAtUtc)}
                    </td>
                    <td style={{ padding: '10px 12px', color: colors.subText, fontSize: 13, fontWeight: 800 }}>{formatDateTime(u.lastLoginAtUtc)}</td>
                    <td style={{ padding: '10px 12px', color: colors.text, fontSize: 13, fontWeight: 900 }}>{u.loginCount ?? 0}</td>
                    <td
                      style={{
                        padding: '10px 12px',
                        color: colors.subText,
                        fontSize: 13,
                        fontWeight: 800,
                        maxWidth: 360,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                      title={u.notes ?? ''}
                    >
                      {u.notes ?? ''}
                    </td>
                  </tr>
                );
              })}

              {!loading && users && filteredUsers.length === 0 ? (
                <tr>
                  <td colSpan={16} style={{ padding: 14, color: colors.subText, fontWeight: 900 }}>
                    No data
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>

        <div
          id="admin-users-paging"
          style={{
            borderTop: `1px solid ${colors.border}`,
            padding: 12,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 10,
            flexWrap: 'wrap',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <button type="button" style={buttonStyle('neutral', pageSafe <= 1)} disabled={pageSafe <= 1} onClick={() => setPage(1)}>
              First
            </button>
            <button type="button" style={buttonStyle('neutral', pageSafe <= 1)} disabled={pageSafe <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
              Prev
            </button>
            <div style={{ color: colors.subText, fontWeight: 900, fontSize: 13 }}>
              Page {pageSafe} of {totalPages}
            </div>
            <button
              type="button"
              style={buttonStyle('neutral', pageSafe >= totalPages)}
              disabled={pageSafe >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            >
              Next
            </button>
            <button
              type="button"
              style={buttonStyle('neutral', pageSafe >= totalPages)}
              disabled={pageSafe >= totalPages}
              onClick={() => setPage(totalPages)}
            >
              Last
            </button>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ color: colors.subText, fontWeight: 900, fontSize: 12 }}>Page size</div>
            <select
              value={pageSize}
              onChange={(e) => {
                const next = Number(e.target.value);
                setPageSize(Number.isFinite(next) ? next : 10);
                setPage(1);
              }}
              style={{ ...inputStyle, width: 110 }}
            >
              {[10, 20, 50, 100].map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Add user modal */}
      {isAddOpen ? (
        <div
          id="admin-users-add-modal"
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0,0,0,0.45)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 16,
            zIndex: 50,
          }}
          onMouseDown={() => {
            if (!isAdding) setIsAddOpen(false);
          }}
        >
          <div
            style={{
              width: '100%',
              maxWidth: 720,
              backgroundColor: colors.card,
              border: `1px solid ${colors.border}`,
              borderRadius: 12,
              padding: 14,
              boxSizing: 'border-box',
            }}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
              <div style={{ fontSize: 16, fontWeight: 900, color: colors.text }}>Add user</div>
              <button type="button" style={buttonStyle('neutral', isAdding)} disabled={isAdding} onClick={() => setIsAddOpen(false)}>
                Close
              </button>
            </div>

            <div
              style={{
                marginTop: 12,
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                gap: 10,
              }}
            >
              <div>
                <div style={{ fontSize: 12, fontWeight: 900, color: colors.subText, marginBottom: 6 }}>Username *</div>
                <input value={newUser.username} onChange={(e) => setNewUser((p) => ({ ...p, username: e.target.value }))} style={inputStyle} />
              </div>
              <div>
                <div style={{ fontSize: 12, fontWeight: 900, color: colors.subText, marginBottom: 6 }}>Password *</div>
                <input
                  type="password"
                  value={newUser.password}
                  onChange={(e) => setNewUser((p) => ({ ...p, password: e.target.value }))}
                  style={inputStyle}
                />
              </div>
              <div>
                <div style={{ fontSize: 12, fontWeight: 900, color: colors.subText, marginBottom: 6 }}>First name</div>
                <input value={newUser.firstName ?? ''} onChange={(e) => setNewUser((p) => ({ ...p, firstName: e.target.value }))} style={inputStyle} />
              </div>
              <div>
                <div style={{ fontSize: 12, fontWeight: 900, color: colors.subText, marginBottom: 6 }}>Last name</div>
                <input value={newUser.lastName ?? ''} onChange={(e) => setNewUser((p) => ({ ...p, lastName: e.target.value }))} style={inputStyle} />
              </div>
              <div>
                <div style={{ fontSize: 12, fontWeight: 900, color: colors.subText, marginBottom: 6 }}>Email</div>
                <input value={newUser.email ?? ''} onChange={(e) => setNewUser((p) => ({ ...p, email: e.target.value }))} style={inputStyle} />
              </div>
              <div>
                <div style={{ fontSize: 12, fontWeight: 900, color: colors.subText, marginBottom: 6 }}>Phone</div>
                <input value={newUser.phone ?? ''} onChange={(e) => setNewUser((p) => ({ ...p, phone: e.target.value }))} style={inputStyle} />
              </div>
              <div>
                <div style={{ fontSize: 12, fontWeight: 900, color: colors.subText, marginBottom: 6 }}>verificationceode</div>
                <input
                  value={newUser.verificationceode ?? ''}
                  maxLength={6}
                  inputMode="numeric"
                  pattern="[0-9]{6}"
                  onChange={(e) => setNewUser((p) => ({ ...p, verificationceode: e.target.value.replace(/\D/g, '').slice(0, 6) }))}
                  style={inputStyle}
                />
              </div>
              <div>
                <div style={{ fontSize: 12, fontWeight: 900, color: colors.subText, marginBottom: 6 }}>Group</div>
                <input
                  list="admin-users-group-options"
                  value={newUser.userGroup ?? ''}
                  onChange={(e) => setNewUser((p) => ({ ...p, userGroup: e.target.value }))}
                  style={inputStyle}
                />
              </div>
              <div>
                <div style={{ fontSize: 12, fontWeight: 900, color: colors.subText, marginBottom: 6 }}>Active</div>
                <select
                  value={newUser.isActive === false ? 'no' : 'yes'}
                  onChange={(e) => setNewUser((p) => ({ ...p, isActive: e.target.value === 'yes' }))}
                  style={inputStyle}
                >
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                </select>
              </div>
              <div>
                <div style={{ fontSize: 12, fontWeight: 900, color: colors.subText, marginBottom: 6 }}>Subscription ends</div>
                <input
                  type="datetime-local"
                  value={newUser.subscriptionEndsAtUtc ?? ''}
                  onChange={(e) => setNewUser((p) => ({ ...p, subscriptionEndsAtUtc: e.target.value || null }))}
                  style={inputStyle}
                />
              </div>
              <div>
                <div style={{ fontSize: 12, fontWeight: 900, color: colors.subText, marginBottom: 6 }}>Acquisition channel</div>
                <input
                  value={newUser.acquisitionChannel ?? ''}
                  onChange={(e) => setNewUser((p) => ({ ...p, acquisitionChannel: e.target.value }))}
                  style={inputStyle}
                />
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <div style={{ fontSize: 12, fontWeight: 900, color: colors.subText, marginBottom: 6 }}>Notes</div>
                <textarea
                  value={newUser.notes ?? ''}
                  onChange={(e) => setNewUser((p) => ({ ...p, notes: e.target.value }))}
                  style={{
                    ...inputStyle,
                    height: 90,
                    padding: 10,
                    resize: 'vertical',
                    fontWeight: 700,
                  }}
                />
              </div>
            </div>

            {addError ? (
              <div style={{ marginTop: 10, color: isDarkMode ? '#FCA5A5' : '#B91C1C', fontWeight: 900 }}>{addError}</div>
            ) : null}

            <div style={{ display: 'flex', gap: 10, marginTop: 12, justifyContent: 'flex-start' }}>
              <button type="button" style={buttonStyle('success', isAdding)} disabled={isAdding} onClick={submitAddUser}>
                {isAdding ? 'Saving…' : 'Save'}
              </button>
              <button type="button" style={buttonStyle('neutral', isAdding)} disabled={isAdding} onClick={() => setIsAddOpen(false)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {/* Edit user modal */}
      {isEditOpen && editUser ? (
        <div
          id="admin-users-edit-modal"
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0,0,0,0.45)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 16,
            zIndex: 50,
          }}
          onMouseDown={() => {
            if (!isUpdating) setIsEditOpen(false);
          }}
        >
          <div
            style={{
              width: '100%',
              maxWidth: 720,
              backgroundColor: colors.card,
              border: `1px solid ${colors.border}`,
              borderRadius: 12,
              padding: 14,
              boxSizing: 'border-box',
            }}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
              <div style={{ fontSize: 16, fontWeight: 900, color: colors.text }}>
                Edit user • {editUser.username} (#{editUser.id})
              </div>
              <button type="button" style={buttonStyle('neutral', isUpdating)} disabled={isUpdating} onClick={() => setIsEditOpen(false)}>
                Close
              </button>
            </div>

            <div
              style={{
                marginTop: 12,
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                gap: 10,
              }}
            >
              <div>
                <div style={{ fontSize: 12, fontWeight: 900, color: colors.subText, marginBottom: 6 }}>Username</div>
                <input value={editDraft.username ?? ''} onChange={(e) => setEditDraft((p) => ({ ...p, username: e.target.value }))} style={inputStyle} />
              </div>
              <div>
                <div style={{ fontSize: 12, fontWeight: 900, color: colors.subText, marginBottom: 6 }}>First name</div>
                <input value={editDraft.firstName ?? ''} onChange={(e) => setEditDraft((p) => ({ ...p, firstName: e.target.value }))} style={inputStyle} />
              </div>
              <div>
                <div style={{ fontSize: 12, fontWeight: 900, color: colors.subText, marginBottom: 6 }}>Last name</div>
                <input value={editDraft.lastName ?? ''} onChange={(e) => setEditDraft((p) => ({ ...p, lastName: e.target.value }))} style={inputStyle} />
              </div>
              <div>
                <div style={{ fontSize: 12, fontWeight: 900, color: colors.subText, marginBottom: 6 }}>Email</div>
                <input value={editDraft.email ?? ''} onChange={(e) => setEditDraft((p) => ({ ...p, email: e.target.value }))} style={inputStyle} />
              </div>
              <div>
                <div style={{ fontSize: 12, fontWeight: 900, color: colors.subText, marginBottom: 6 }}>Phone</div>
                <input value={editDraft.phone ?? ''} onChange={(e) => setEditDraft((p) => ({ ...p, phone: e.target.value }))} style={inputStyle} />
              </div>
              <div>
                <div style={{ fontSize: 12, fontWeight: 900, color: colors.subText, marginBottom: 6 }}>verificationceode</div>
                <input
                  value={editDraft.verificationceode ?? ''}
                  maxLength={6}
                  inputMode="numeric"
                  pattern="[0-9]{6}"
                  onChange={(e) => setEditDraft((p) => ({ ...p, verificationceode: e.target.value.replace(/\D/g, '').slice(0, 6) }))}
                  style={inputStyle}
                />
              </div>
              <div>
                <div style={{ fontSize: 12, fontWeight: 900, color: colors.subText, marginBottom: 6 }}>Group</div>
                <input
                  list="admin-users-group-options"
                  value={editDraft.userGroup ?? ''}
                  onChange={(e) => setEditDraft((p) => ({ ...p, userGroup: e.target.value }))}
                  style={inputStyle}
                />
              </div>
              <div>
                <div style={{ fontSize: 12, fontWeight: 900, color: colors.subText, marginBottom: 6 }}>Failed attempts</div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <input
                    type="number"
                    min={0}
                    value={String(editDraft.verificationceodeFailedAttempts ?? 0)}
                    onChange={(e) => {
                      const next = Number(e.target.value);
                      setEditDraft((p) => ({
                        ...p,
                        verificationceodeFailedAttempts: Number.isFinite(next) ? Math.max(0, Math.trunc(next)) : 0,
                      }));
                    }}
                    style={{ ...inputStyle, flex: 1 }}
                  />
                  <button
                    type="button"
                    title="Reset failed verification attempts to zero."
                    style={buttonStyle('neutral', isUpdating)}
                    disabled={isUpdating || (editDraft.verificationceodeFailedAttempts ?? 0) === 0}
                    onClick={() => setEditDraft((p) => ({ ...p, verificationceodeFailedAttempts: 0 }))}
                  >
                    Reset
                  </button>
                </div>
              </div>
              <div>
                <div style={{ fontSize: 12, fontWeight: 900, color: colors.subText, marginBottom: 6 }}>Active</div>
                <select
                  value={editDraft.isActive === false ? 'no' : 'yes'}
                  onChange={(e) => setEditDraft((p) => ({ ...p, isActive: e.target.value === 'yes' }))}
                  style={inputStyle}
                >
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                </select>
              </div>
              <div>
                <div style={{ fontSize: 12, fontWeight: 900, color: colors.subText, marginBottom: 6 }}>Subscription ends</div>
                <input
                  type="datetime-local"
                  value={editDraft.subscriptionEndsAtUtc ?? ''}
                  onChange={(e) => setEditDraft((p) => ({ ...p, subscriptionEndsAtUtc: e.target.value || null }))}
                  style={inputStyle}
                />
              </div>
              <div>
                <div style={{ fontSize: 12, fontWeight: 900, color: colors.subText, marginBottom: 6 }}>EULA accepted</div>
                <input
                  type="datetime-local"
                  value={editDraft.eulaAcceptedAtUtc ?? ''}
                  onChange={(e) => setEditDraft((p) => ({ ...p, eulaAcceptedAtUtc: e.target.value || null }))}
                  style={inputStyle}
                />
              </div>
              <div>
                <div style={{ fontSize: 12, fontWeight: 900, color: colors.subText, marginBottom: 6 }}>Acquisition channel</div>
                <input
                  value={editDraft.acquisitionChannel ?? ''}
                  onChange={(e) => setEditDraft((p) => ({ ...p, acquisitionChannel: e.target.value }))}
                  style={inputStyle}
                />
              </div>
              <div>
                <div style={{ fontSize: 12, fontWeight: 900, color: colors.subText, marginBottom: 6 }}>Created</div>
                <input value={formatDateTime(editUser.createdAtUtc)} style={inputStyle} disabled />
              </div>
              <div>
                <div style={{ fontSize: 12, fontWeight: 900, color: colors.subText, marginBottom: 6 }}>Last login</div>
                <input value={formatDateTime(editUser.lastLoginAtUtc)} style={inputStyle} disabled />
              </div>
              <div>
                <div style={{ fontSize: 12, fontWeight: 900, color: colors.subText, marginBottom: 6 }}>Login count</div>
                <input value={String(editUser.loginCount ?? 0)} style={inputStyle} disabled />
              </div>
              <div>
                <div style={{ fontSize: 12, fontWeight: 900, color: colors.subText, marginBottom: 6 }}>Deleted</div>
                <input value={editUser.isDeleted ? 'Yes' : 'No'} style={inputStyle} disabled />
              </div>
              <div>
                <div style={{ fontSize: 12, fontWeight: 900, color: colors.subText, marginBottom: 6 }}>Last selected stock</div>
                <input value={editUser.lastSelectedStockSymbol ?? ''} style={inputStyle} disabled />
              </div>
              <div>
                <div style={{ fontSize: 12, fontWeight: 900, color: colors.subText, marginBottom: 6 }}>Last selected at</div>
                <input value={formatDateTime(editUser.lastSelectedStockAtUtc)} style={inputStyle} disabled />
              </div>
              <div>
                <div style={{ fontSize: 12, fontWeight: 900, color: colors.subText, marginBottom: 6 }}>Portfolio group by stock</div>
                <input value={editUser.portfolioGroupByStock ? 'Yes' : 'No'} style={inputStyle} disabled />
              </div>
              <div>
                <div style={{ fontSize: 12, fontWeight: 900, color: colors.subText, marginBottom: 6 }}>Portfolio last tab</div>
                <input value={editUser.portfolioLastTab ?? ''} style={inputStyle} disabled />
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <div style={{ fontSize: 12, fontWeight: 900, color: colors.subText, marginBottom: 6 }}>Notes</div>
                <textarea
                  value={editDraft.notes ?? ''}
                  onChange={(e) => setEditDraft((p) => ({ ...p, notes: e.target.value }))}
                  style={{
                    ...inputStyle,
                    height: 90,
                    padding: 10,
                    resize: 'vertical',
                    fontWeight: 700,
                  }}
                />
              </div>
              <div style={{ gridColumn: '1 / -1', display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                <div style={{ minWidth: 220, flex: 1 }}>
                  <div style={{ fontSize: 12, fontWeight: 900, color: colors.subText, marginBottom: 6 }}>Reset password</div>
                  <input
                    type="password"
                    value={resetPassword}
                    onChange={(e) => setResetPassword(e.target.value)}
                    style={inputStyle}
                  />
                </div>
                <button
                  type="button"
                  title="Sets a new password for the selected user."
                  style={buttonStyle('primary', isResetting)}
                  disabled={isResetting}
                  onClick={submitResetPassword}
                >
                  {isResetting ? 'Resetting…' : 'Reset password'}
                </button>
              </div>
            </div>

            {editError ? (
              <div style={{ marginTop: 10, color: isDarkMode ? '#FCA5A5' : '#B91C1C', fontWeight: 900 }}>{editError}</div>
            ) : null}
            {resetError ? (
              <div style={{ marginTop: 10, color: isDarkMode ? '#FCA5A5' : '#B91C1C', fontWeight: 900 }}>{resetError}</div>
            ) : null}

            <div style={{ display: 'flex', gap: 10, marginTop: 12, justifyContent: 'flex-start' }}>
              <button type="button" style={buttonStyle('success', isUpdating)} disabled={isUpdating} onClick={submitEditUser}>
                {isUpdating ? 'Saving…' : 'Save'}
              </button>
              <button type="button" style={buttonStyle('neutral', isUpdating)} disabled={isUpdating} onClick={() => setIsEditOpen(false)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default AdminUsersScreen;
